import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MailPlus, Save, Eye, RotateCcw, Info, Globe2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const EVENT_LABELS = {
  shift_assigned:  { label: "New shift assigned",  hint: "Sent to the employee whenever a work shift is put on their schedule." },
  open_shift:      { label: "Open shift available", hint: "Sent to every rostered employee at the facility when a slot is cleared." },
  request_decided: { label: "Request approved / denied", hint: "Sent to the requester after a PTO / trade / pickup / modification decision." },
  manager_pending: { label: "New request for approval", hint: "Sent to every manager of the facility when a new request lands." },
};

/**
 * Notification message templates editor.  Managers can edit templates for the
 * facility they're currently viewing.  Admins can additionally toggle to the
 * global fallback (used when no facility override exists).
 */
export default function NotificationTemplatesPage({ facilityId, facilityLabel }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [scope, setScope] = useState("facility"); // "facility" | "global"
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [preview, setPreview] = useState({}); // {event: {subject, body}}

  const activeFacility = scope === "facility" ? facilityId : null;

  const load = async () => {
    setLoading(true);
    try {
      const q = activeFacility ? `?facility_id=${activeFacility}` : "";
      const { data } = await api.get(`/notification-templates${q}`);
      // Seed the editable fields with the facility override if any, else with
      // the effective text — so admins/managers can start from what's currently
      // shipping and tweak from there.
      setRows((data || []).map(r => {
        const override = scope === "facility" ? r.facility_override : r.global_override;
        return {
          ...r,
          draft_subject: (override?.subject ?? r.effective.subject) || "",
          draft_body:    (override?.body    ?? r.effective.body)    || "",
          initial_subject: (override?.subject ?? r.effective.subject) || "",
          initial_body:    (override?.body    ?? r.effective.body)    || "",
        };
      }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load templates");
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeFacility]);

  const setDraft = (ev, k, v) => {
    setRows(prev => prev.map(r => r.event === ev ? { ...r, [k]: v } : r));
  };

  const dirty = (r) => r.draft_subject !== r.initial_subject || r.draft_body !== r.initial_body;

  const save = async (r) => {
    setSaving(r.event);
    try {
      const q = activeFacility ? `?facility_id=${activeFacility}` : "";
      await api.put(`/notification-templates/${r.event}${q}`, {
        subject: r.draft_subject, body: r.draft_body,
      });
      toast.success("Template saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    }
    setSaving(null);
  };

  const resetToDefault = async (r) => {
    if (!window.confirm(`Reset "${EVENT_LABELS[r.event].label}" template to the default? This clears your override.`)) return;
    setSaving(r.event);
    try {
      const q = activeFacility ? `?facility_id=${activeFacility}` : "";
      await api.put(`/notification-templates/${r.event}${q}`, { subject: "", body: "" });
      toast.success("Reverted to default");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Reset failed");
    }
    setSaving(null);
  };

  const doPreview = async (r) => {
    try {
      const q = activeFacility ? `?facility_id=${activeFacility}` : "";
      const { data } = await api.post(`/notification-templates/${r.event}/preview${q}`, {
        subject: r.draft_subject, body: r.draft_body,
      });
      setPreview(p => ({ ...p, [r.event]: data }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Preview failed");
    }
  };

  const insertPlaceholder = (ev, target, ph) => {
    const key = target === "subject" ? "draft_subject" : "draft_body";
    setRows(prev => prev.map(r => r.event === ev ? { ...r, [key]: (r[key] || "") + `{${ph}}` } : r));
  };

  return (
    <div className="space-y-4" data-testid="notification-templates">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 flex items-start gap-3">
        <MailPlus size={20} className="text-blue-600 mt-0.5" />
        <div className="flex-1">
          <div className="font-display font-semibold text-lg">Notification message templates</div>
          <div className="text-sm text-slate-500">
            Customize the subject and body that goes out for every notification. Use{" "}
            <code className="text-xs bg-slate-100 px-1 rounded">{"{placeholder}"}</code> tokens — click the chips below each event to insert them.
            Empty templates fall back to the built-in defaults.
          </div>
        </div>
        {isAdmin && (
          <div className="flex bg-slate-100 rounded-full p-0.5 text-xs" data-testid="template-scope-toggle">
            <button
              className={`px-3 py-1 rounded-full inline-flex items-center gap-1 ${scope === "facility" ? "bg-white shadow font-semibold text-slate-800" : "text-slate-500"}`}
              onClick={() => setScope("facility")}
              data-testid="scope-facility"
            >
              <Building2 size={12} /> {facilityLabel || "Facility"}
            </button>
            <button
              className={`px-3 py-1 rounded-full inline-flex items-center gap-1 ${scope === "global" ? "bg-white shadow font-semibold text-slate-800" : "text-slate-500"}`}
              onClick={() => setScope("global")}
              data-testid="scope-global"
            >
              <Globe2 size={12} /> Global default
            </button>
          </div>
        )}
      </div>

      {loading && <div className="text-sm text-slate-500 p-6">Loading templates…</div>}
      {!loading && rows.map(r => {
        const label = EVENT_LABELS[r.event]?.label || r.event;
        const hint  = EVENT_LABELS[r.event]?.hint || "";
        const source = r.effective?.source;
        return (
          <div key={r.event} className="rounded-2xl border border-slate-200 bg-white" data-testid={`tmpl-${r.event}`}>
            <div className="p-4 border-b border-slate-100 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold">{label}</div>
                <div className="text-xs text-slate-500">{hint}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-semibold shrink-0 ${
                source === "facility" ? "bg-blue-100 text-blue-800"
                : source === "global" ? "bg-amber-100 text-amber-800"
                : "bg-slate-100 text-slate-600"
              }`} data-testid={`tmpl-source-${r.event}`}>
                {source === "facility" ? "Facility override"
                : source === "global"  ? "Global override"
                : "Default"}
              </span>
            </div>

            <div className="p-4 space-y-3">
              <div>
                <Label className="text-xs">Subject</Label>
                <Input
                  value={r.draft_subject}
                  onChange={(e) => setDraft(r.event, "draft_subject", e.target.value)}
                  data-testid={`tmpl-subject-${r.event}`}
                />
              </div>
              <div>
                <Label className="text-xs">Body</Label>
                <Textarea
                  rows={4}
                  value={r.draft_body}
                  onChange={(e) => setDraft(r.event, "draft_body", e.target.value)}
                  className="font-mono text-sm"
                  data-testid={`tmpl-body-${r.event}`}
                />
              </div>

              <div className="flex flex-wrap gap-1.5 items-center">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 mr-1">Placeholders:</span>
                {(r.placeholders || []).map(p => (
                  <span key={p} className="text-[11px] rounded-md border border-slate-200 bg-slate-50 text-slate-700 pl-2 pr-0 py-0.5 inline-flex items-center gap-1" data-testid={`tmpl-ph-${r.event}-${p}`}>
                    <code>{`{${p}}`}</code>
                    <button
                      title="Insert in subject"
                      className="text-slate-400 hover:text-blue-700 px-1"
                      onClick={() => insertPlaceholder(r.event, "subject", p)}
                    >
                      s+
                    </button>
                    <button
                      title="Insert in body"
                      className="text-slate-400 hover:text-blue-700 px-1 border-l border-slate-200"
                      onClick={() => insertPlaceholder(r.event, "body", p)}
                    >
                      b+
                    </button>
                  </span>
                ))}
              </div>

              {preview[r.event] && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid={`tmpl-preview-${r.event}`}>
                  <div className="text-[10px] uppercase tracking-widest text-blue-700 font-semibold mb-1 flex items-center gap-1">
                    <Info size={11} /> Preview with sample data
                  </div>
                  <div className="text-sm font-semibold text-slate-800">{preview[r.event].subject}</div>
                  <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans mt-1">{preview[r.event].body}</pre>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-1">
                <Button variant="outline" onClick={() => doPreview(r)} data-testid={`tmpl-preview-btn-${r.event}`}>
                  <Eye size={14} className="mr-1" /> Preview
                </Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={!dirty(r) || saving === r.event}
                  onClick={() => save(r)}
                  data-testid={`tmpl-save-${r.event}`}
                >
                  <Save size={14} className="mr-1" /> {saving === r.event ? "Saving…" : "Save"}
                </Button>
                {(scope === "facility" ? r.facility_override : r.global_override) && (
                  <Button
                    variant="ghost"
                    className="text-slate-500 hover:text-red-700"
                    onClick={() => resetToDefault(r)}
                    data-testid={`tmpl-reset-${r.event}`}
                  >
                    <RotateCcw size={14} className="mr-1" /> Reset to default
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
